<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'

import { usePagination } from '@/composables/usePagination'
import { useSearch } from '@/composables/useSearch'
import {
  getDemandPage,
  assignDemand,
  claimDemand,
  rejectDemand,
  closeDemand
} from '@/api/demand'
import { createSingleConversation } from '@/api/chat'

import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import DemandCard from './components/DemandCard.vue'
import DemandAssignModal from './components/DemandAssignModal.vue'

const router = useRouter()

// 数据
const loading = ref(false)
const demandList = ref([])

// 分页
const { pagination, goToPage, setPageSize, updatePagination, getPaginationParams } = usePagination()

// 搜索
const { searchForm, handleSearch: triggerSearch, resetSearch, getSearchParams } = useSearch({
  initialForm: {
    status: ''
  },
  onSearch: fetchData
})

// 分配窗口
const showAssignModal = ref(false)
const assignItem = ref(null)
const assignModalRef = ref()

// status summary
const statusCounts = ref({
  pending: 0,
  processing: 0,
  completed: 0,
  closed: 0
})

// 状态选项：0-待处理，1-处理中，2-已完成，3-已关闭
const statusOptions = [
  { label: '全部状态', value: '' },
  { label: '待处理', value: 0 },
  { label: '处理中', value: 1 },
  { label: '已完成', value: 2 },
  { label: '已关闭', value: 3 }
]

// 获取数据
async function fetchData() {
  loading.value = true
  try {
    const params = {
      ...getSearchParams(),
      ...getPaginationParams()
    }
    const data = await getDemandPage(params)
    demandList.value = data.records || []
    updatePagination(data)
    // 更新统计（如果后端返回）
    if (data.statusCounts) {
      statusCounts.value = data.statusCounts
    }
  } catch (error) {
    ElMessage.error('加载需求列表失败')
    console.error('加载需求列表失败', error)
  } finally {
    loading.value = false
  }
}

// 筛选变化
const handleFilterChange = () => {
  pagination.current = 1
  fetchData()
}

// 换页
const changePage = (page) => {
  goToPage(page)
  fetchData()
}

// 切换每页条数
const changePageSize = (size) => {
  setPageSize(size)
  fetchData()
}

// ========== 操作 ==========

// 分配
const handleAssign = (item) => {
  assignItem.value = item
  showAssignModal.value = true
}

const handleAssignConfirm = async ({ demandId, handlerId }) => {
  try {
    await assignDemand(demandId, handlerId)
    ElMessage.success('分配成功')
    assignModalRef.value?.done()
    fetchData()
  } catch (error) {
    ElMessage.error(error.message || '分配失败')
    assignModalRef.value?.done()
  }
}

// 认领
const handleClaim = async (item) => {
  try {
    await ElMessageBox.confirm('确定认领此需求吗？认领后您将成为此需求的处理人。', '确认认领', {
      confirmButtonText: '确认认领',
      cancelButtonText: '取消',
      type: 'info'
    })
    await claimDemand(item.id)
    ElMessage.success('认领成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '认领失败')
    }
  }
}

// 拒绝
const handleReject = async (item) => {
  try {
    const { value: remark } = await ElMessageBox.prompt('请输入拒绝原因（可选）', '拒绝需求', {
      confirmButtonText: '确认拒绝',
      cancelButtonText: '取消',
      inputType: 'textarea',
      inputPlaceholder: '请输入拒绝原因...',
      type: 'warning'
    })
    await rejectDemand(item.id, remark)
    ElMessage.success('已拒绝')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '操作失败')
    }
  }
}

// 关闭
const handleClose = async (item) => {
  try {
    await ElMessageBox.confirm('确定关闭此需求吗？', '关闭需求', {
      confirmButtonText: '确认关闭',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await closeDemand(item.id)
    ElMessage.success('已关闭')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '操作失败')
    }
  }
}

// 发起聊天
const handleChat = async (item) => {
  if (!item.userId) {
    ElMessage.warning('无法获取用户信息')
    return
  }
  try {
    const conversation = await createSingleConversation(item.userId)
    const conversationId = conversation?.id || conversation
    router.push({ path: '/chat', query: { conversationId } })
  } catch (error) {
    ElMessage.error(error.message || '创建会话失败')
  }
}

// 查看聊天记录
const handleViewChat = (item) => {
  if (item.conversationId) {
    router.push({ path: '/chat', query: { conversationId: item.conversationId } })
  }
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <ListPageShell>
    <!-- 页面标题和统计 -->
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="flex items-center justify-between mb-0 gap-3 flex-wrap">
      <div>
        <h1 class="text-xl font-semibold text-slate-900">用户需求管理</h1>
        <p class="text-sm text-slate-500 mt-1">管理C端用户提交的需求，分配处理人或自行认领</p>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
        <span class="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
          待分配：{{ statusCounts.pending }}
        </span>
        <span class="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          处理中：{{ statusCounts.processing }}
        </span>
        <span class="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
          已完成：{{ statusCounts.completed }}
        </span>
      </div>
        </div>
      </div>
    </template>

    <!-- 筛选栏 -->
    <template #filters>
      <PageFilterBar
      body-class="filter-bar-body is-dense"
      content-class="filter-bar-content is-spacious"
      show-refresh
      :refresh-loading="loading"
      @refresh="fetchData"
    >
      <div class="flex items-center gap-4 flex-wrap">
        <!-- 搜索框 -->
        <div class="relative flex-1 min-w-[200px]">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            v-model="searchForm.keyword"
            type="text"
            placeholder="搜索需求描述、联系人..."
            class="form-search-input form-control-sm w-full"
            @keyup.enter="triggerSearch"
          />
        </div>

        <!-- 状态筛选 -->
        <select
          v-model="searchForm.status"
          class="form-select-native form-control-sm"
          @change="handleFilterChange"
        >
          <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- 搜索 & 重置 -->
      </div>

      <template #searchActions>
        <button
          @click="triggerSearch"
          class="btn-primary"
        >
          搜索
        </button>
        <button
          @click="resetSearch"
          class="btn-secondary"
        >
          重置
        </button>
      </template>
      </PageFilterBar>
    </template>

    <!-- 结果信息 -->
    <div class="flex items-center justify-between mb-4">
      <span class="text-sm text-slate-500">共 {{ pagination.total }} 条需求</span>
    </div>

    <!-- 卡片列表 -->
    <div v-if="!loading && demandList.length > 0" class="space-y-4">
      <DemandCard
        v-for="item in demandList"
        :key="item.id"
        :item="item"
        @assign="handleAssign"
        @claim="handleClaim"
        @reject="handleReject"
        @close="handleClose"
        @chat="handleChat"
        @view-chat="handleViewChat"
      />
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="space-y-4">
      <div v-for="i in 3" :key="i" class="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
        <div class="flex items-center gap-3 mb-3">
          <div class="w-2 h-2 bg-slate-200 rounded-full"></div>
          <div class="h-4 bg-slate-200 rounded w-16"></div>
          <div class="h-4 bg-slate-200 rounded w-16"></div>
        </div>
        <div class="flex items-center gap-2.5 mb-3">
          <div class="w-8 h-8 bg-slate-200 rounded-full"></div>
          <div class="h-4 bg-slate-200 rounded w-24"></div>
          <div class="h-4 bg-slate-200 rounded w-28"></div>
        </div>
        <div class="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
        <div class="h-4 bg-slate-200 rounded w-1/2"></div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="!loading && demandList.length === 0" class="flex flex-col items-center justify-center py-20">
      <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
        </svg>
      </div>
      <p class="text-slate-500 text-sm mb-1">暂无用户需求</p>
      <p class="text-slate-400 text-xs">等待C端用户提交需求后将在此展示</p>
    </div>

    <!-- 分页 -->
    <AppPagination
      v-if="pagination.total > 0"
      class="pagination-bridge"
      :pagination="pagination"
      :page-sizes="[10, 20, 50]"
      @page-change="changePage"
      @size-change="changePageSize"
    />
    <DemandAssignModal
      ref="assignModalRef"
      v-model:show="showAssignModal"
      :item="assignItem"
      @confirm="handleAssignConfirm"
    />
  </ListPageShell>
</template>
